/** Answer distribution. Plain divs, no chart library (the student bundle budget is 150 kB).
 * Modes: live (bars grow as responses arrive), closed, revealed (correct highlighted, others
 * dimmed), paired (before/after a peer-instruction re-vote). Percentages are always printed:
 * colour and length never carry the number alone. Projector variant is bigger. */
import { useTranslation } from "react-i18next";

export type Bar = { key: string; label: string; count: number; correct?: boolean; before?: number };

type Props = {
  bars: Bar[];
  total: number;
  mode?: "live" | "closed" | "revealed" | "paired";
  size?: "control" | "projector";
  /** Show absolute counts next to percentages (teacher setting for the projector). */
  counts?: boolean;
};

const pct = (n: number, total: number) => (total ? Math.round((100 * n) / total) : 0);

export function DistributionBars({ bars, total, mode = "live", size = "control", counts = true }: Props) {
  const { t } = useTranslation();
  const big = size === "projector";
  return (
    <ul
      className="flex flex-col gap-2"
      aria-label={t("bars.label")}
    >
      {bars.map((b) => {
        const p = pct(b.count, total);
        const dim = mode === "revealed" && !b.correct;
        const fill = mode === "revealed" && b.correct ? "var(--secure)" : "var(--accent)";
        return (
          <li key={b.key} className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
            <span className={big ? "text-5xl font-semibold" : "text-base font-semibold"}>{b.label}</span>
            <div className="relative">
              {mode === "paired" && b.before !== undefined && (
                <div
                  aria-hidden
                  className="mb-1 h-3 rounded-sm"
                  style={{ width: `${pct(b.before, total)}%`, background: "var(--ink-2)", opacity: 0.4 }}
                />
              )}
              <div
                aria-hidden
                className={[big ? "h-16" : "h-6", "rounded-sm transition-[width,opacity] duration-250 ease-out motion-reduce:transition-none"].join(" ")}
                style={{ width: p ? `${p}%` : "3px", background: fill, opacity: dim ? 0.35 : 1 }}
              />
            </div>
            <span className={[big ? "text-[40px] leading-none" : "text-sm", "tabular-nums", dim ? "text-[color:var(--ink-2)]" : ""].join(" ")}>
              {p}%{counts && <span className="text-[color:var(--ink-2)]"> · {b.count}</span>}
              {mode === "revealed" && b.correct && <span> ✓<span className="sr-only"> {t("bars.correct")}</span></span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
